import { tokens } from "@/tokens";
import { FC, ReactElement, useState, useMemo } from "react";
import { Box, Button, Stack, Typography } from "@mui/material";

import { ChangePasswordModal } from "@/components";
import { useAppSelector } from "@/store";
import { AccountForm, CompanyForm } from "./components";
import { roles } from "@/roles";

const AccountSettings: FC = (): ReactElement => {
  const { user } = useAppSelector((state) => state.auth);
  const [changePassModalState, setChangePassModalState] = useState(false);
  const handleChangePasswordModalOpen = () => {
    setChangePassModalState(true);
  };

  const isTDAdmin = useMemo(() => {
    return user?.userRole === roles.TDADMIN;
  }, [user]);

  return (
    <>
      <Box px={2.5} pt={2} pb={1.5}>
        <Typography
          sx={{
            fontWeight: tokens.fontWeight.bold,
            color: tokens.text.primary,
            fontSize: tokens.fontSize.xl,
          }}
        >
          Account Settings
        </Typography>
        <Typography sx={{ fontSize: tokens.fontSize.base, color: tokens.text.muted, mt: 0.25 }}>
          Manage your personal information and preferences
        </Typography>
      </Box>

      <Box sx={{ borderTop: `1px solid ${tokens.border.light}`, px: 2.5, py: 2 }}>
        <AccountForm user={user} />
      </Box>

      {!isTDAdmin && (
        <Box sx={{ borderTop: `1px solid ${tokens.border.light}`, px: 2.5, py: 2 }}>
          <Typography
            sx={{
              fontWeight: tokens.fontWeight.bold,
              color: tokens.text.primary,
              fontSize: tokens.fontSize.md,
              mb: 0.25,
            }}
          >
            Company
          </Typography>
          <Typography sx={{ fontSize: tokens.fontSize.base, color: tokens.text.muted, mb: 1.5 }}>
            Update your company details
          </Typography>
          <CompanyForm user={user} />
        </Box>
      )}

      <Box sx={{ borderTop: `1px solid ${tokens.border.light}`, px: 2.5, py: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography
              sx={{
                fontWeight: tokens.fontWeight.semibold,
                color: tokens.text.primary,
                fontSize: tokens.fontSize.md,
              }}
            >
              Password
            </Typography>
            <Typography sx={{ fontSize: tokens.fontSize.base, color: tokens.text.muted, mt: 0.25 }}>
              Change your password to keep your account secure
            </Typography>
          </Box>
          <Button
            variant="outlined"
            sx={{
              borderColor: tokens.border.default,
              color: tokens.text.body,
              fontWeight: tokens.fontWeight.semibold,
              fontSize: tokens.fontSize.sm2,
              borderRadius: tokens.radius.md,
              boxShadow: 'none',
              textTransform: 'none',
              height: 32,
              flexShrink: 0,
              '&:hover': { borderColor: tokens.text.disabled, bgcolor: tokens.bg.hover },
            }}
            onClick={handleChangePasswordModalOpen}
          >
            Change Password
          </Button>
        </Stack>
      </Box>

      <ChangePasswordModal
        open={changePassModalState}
        setOpen={setChangePassModalState}
      />
    </>
  );
};

export default AccountSettings;
